/* The nine sections of the visit review workspace.
   Visual reference: docs/design/visit-detail.html. Normative rules: docs/design/spec/.

   Each is a plain component over data the workspace already fetched — ONE section is mounted at a
   time, so nothing here may hold its own long-lived state beyond what the reader is looking at.

   Orders / returns / payments are read-only here: they carry a document status from Excalibur that
   this user does not own. Reports, task answers and price monitoring are the three moderatable
   kinds, and they are the ones the queue above hoists. */

import React, { useMemo, useState } from "react";
import { Button, Card, Empty, Flex, Image, Input, Segmented, Space, Table, Tag, Timeline, Typography } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined, FileImageOutlined, RightOutlined, SearchOutlined } from "@ant-design/icons";

import { PersonCell, TableFoot } from "../../component/table-cells";
import LeafletMap from "../points/ext/LeafletMap";
import { PhotoReview } from "../../models/photo-review";
import { ENTITY_DROPDOWN_SETTINGS, VISIT_PAGE } from "../../config/lang";
import { call } from "../../util/action";
import { fill, translate } from "../../util/util";

import { SectionEmpty, StatusTag, dash, statusOf } from "./parts";

/* ------------------------------------------------------------------ helpers */

const rowsOf = response => (Array.isArray(response) ? response : response?.results || []);

export const fullName = user => (user ? [user.first_name, user.last_name].filter(Boolean).join(" ") : "");

/** Currency is not modelled anywhere in the app; the legacy tabs print a bare number too. */
export const money = value => (value === null || value === undefined || value === "" ? "" : Number(value).toFixed(2));

/* Order and Return statuses are stored as `TextChoices` KEYS, and DRF serialises the key, not the
   label — so the cell rendered `sent_for_delivering`, lowercase and English in all four locales.
   Bordered rather than filled: this is a document status from Excalibur that the supervisor does
   not own, so it must not read like one of our own `.status-tag` verdicts. The label carries the
   `-700` tone (it is text) while the border carries `-500` (spec/01#status-colour). */
const ORDER_STATUS = {
  new: "new",
  processing: "processing",
  sent_for_delivering: "ok",
  sent_to_logistics: "ok",
  closed: "closed",
  canceled: "no",
};

export function OrderStatusTag({ value, lang }) {
  if (!value) return dash;
  const tone = ORDER_STATUS[value] || "new";
  const label = translate(VISIT_PAGE, `order_status_${value}`, lang);
  return (
    <Tag bordered className={`vd-ostatus vd-ostatus--${tone}`}>
      {label || value}
    </Tag>
  );
}

/* Newest first. These endpoints return a per-visit list with no pagination and no ordering — and
   neither `Order` nor `Return` declares a `Meta.ordering`, so the rows arrive in whatever order the
   database felt like. Sorting the loaded list is therefore the whole list, not a page of it. */
const newestFirst = (rows, key = "datetime") => [...rows].sort((a, b) => new Date(b[key] || 0) - new Date(a[key] || 0));

/** `additional_info.tags` is an array, or a bare string when there is exactly one. */
const tagsOf = tags => (Array.isArray(tags) ? tags : typeof tags === "string" && tags ? [tags] : []);

/** A table that scrolls inside itself, so the page never scrolls sideways (spec/02). */
function SectionTable({ columns, rows, onRow, minWidth }) {
  return (
    <Table
      className="vd-table"
      size="small"
      rowKey="id"
      columns={columns}
      dataSource={rows}
      pagination={false}
      scroll={{ x: minWidth }}
      onRow={record => ({ onClick: () => onRow && onRow(record), className: onRow ? "vd-row" : "" })}
    />
  );
}

/**
 * A section's pager — the app's shared `TableFoot`, so the rows-per-page control, the range and the
 * page window read the same here as on every list page.
 *
 * `total` is the caller's, because only the caller knows whether it is the server's `count` or the
 * length of a list already in memory.
 */
function SectionFoot({ id, total, pager }) {
  if (!pager || total <= pager.size) return null;
  return (
    <TableFoot
      id={id}
      page={pager.page}
      size={pager.size}
      total={total}
      sizes={pager.sizes}
      labels={pager.labels}
      onPage={pager.onPage}
      onSize={pager.onSize}
    />
  );
}

/** The trailing chevron every drill-in row carries in the design. */
const openColumn = label => ({
  key: "open",
  width: 56,
  align: "right",
  render: () => <Button type="text" size="small" icon={<RightOutlined />} aria-label={label} />,
});

/* --------------------------------------------------------- orders / returns / payments */

export function OrdersSection({ data, lang, onOpen }) {
  const t = key => translate(VISIT_PAGE, key, lang);
  const rows = rowsOf(data);
  if (!rows.length) return <SectionEmpty text={t("nothing_here")} />;

  return (
    <SectionTable
      minWidth={1180}
      rows={newestFirst(rows)}
      onRow={onOpen}
      columns={[
        { key: "name", title: t("orders_popup_order_id_title"), dataIndex: "name", width: 140, render: value => <b>{value}</b> },
        /* Widthed. It was the only column without one, so it took every spare pixel and left the
           gap the delivery date now fills. */
        { key: "cp", title: t("orders_popup_counterparty_label"), dataIndex: ["counterparty", "name"], width: 240, ellipsis: true },
        /* The counterparty's OWN address, not the visited point's — `Counterparty.address` was on the
           model all along but `CounterpartyShortSerializer` (the one every visit section nests) was
           the only Counterparty serializer not exposing it. */
        {
          key: "cp_address",
          title: t("orders_address_label"),
          dataIndex: ["counterparty", "address"],
          width: 220,
          ellipsis: true,
          render: value => value || dash,
        },
        /* Annotated server-side (`Count("items")`). The list payload carries no `items` — the modal
           fetches the detail for those — so counting client-side would have meant one request per
           row. */
        {
          key: "n",
          title: t("orders_items_count_label"),
          dataIndex: "items_count",
          width: 110,
          align: "right",
          render: value => <span className="vd-num">{value ?? dash}</span>,
        },
        {
          key: "delivery",
          title: t("orders_delivery_label"),
          dataIndex: "delivery_date",
          width: 140,
          render: value => (value ? formatDate(value, lang) : dash),
        },
        { key: "at", title: t("orders_product_status_date"), dataIndex: "datetime", width: 160, render: value => formatDateTime(value, lang) },
        { key: "status", title: t("status_label"), dataIndex: "status", width: 180, render: value => <OrderStatusTag value={value} lang={lang} /> },
        {
          key: "total",
          title: t("orders_product_total"),
          dataIndex: "order_total",
          width: 130,
          align: "right",
          render: value => <span className="vd-num">{money(value)}</span>,
        },
        openColumn(t("view_details")),
      ]}
    />
  );
}

export function ReturnsSection({ data, lang, onOpen }) {
  const t = key => translate(VISIT_PAGE, key, lang);
  const rows = rowsOf(data);
  if (!rows.length) return <SectionEmpty text={t("nothing_here")} />;

  return (
    <SectionTable
      minWidth={1150}
      rows={newestFirst(rows)}
      onRow={onOpen}
      columns={[
        { key: "id", title: t("orders_return_id_label"), dataIndex: "name", width: 130, render: (value, row) => <b>{value || `#${row.id}`}</b> },
        { key: "cp", title: t("orders_popup_counterparty_label"), dataIndex: ["counterparty", "name"], width: 260, ellipsis: true },
        {
          key: "cp_address",
          title: t("orders_address_label"),
          dataIndex: ["counterparty", "address"],
          width: 220,
          ellipsis: true,
          render: value => value || dash,
        },
        /* Counted from the row, no backend change needed: `items` IS nested on the return LIST
           serializer (the detail modal reuses the row rather than fetching), with `items__product`
           prefetched. An order is the opposite case — see its column above. */
        {
          key: "n",
          title: t("orders_items_count_label"),
          width: 110,
          align: "right",
          render: (value, row) => <span className="vd-num">{(row.items || []).length}</span>,
        },
        { key: "at", title: t("orders_return_crated_label"), dataIndex: "datetime", width: 160, render: value => formatDateTime(value, lang) },
        {
          key: "phys",
          title: t("orders_physical_return_label"),
          dataIndex: "date_of_physical_return",
          width: 170,
          render: value => (value ? formatDate(value, lang) : dash),
        },
        /* No Status column: a return's document status is Excalibur's, it never enters the review
           queue, and the detail modal still shows it. A Total belongs here (PICSELL-1572) but the
           web API models neither a return total nor a per-item price — a backend item, not a
           column that could be filled with a wrong number today. */
        openColumn(t("view_details")),
      ]}
    />
  );
}

export function PaymentsSection({ data, lang, onOpen }) {
  const t = key => translate(VISIT_PAGE, key, lang);
  const rows = rowsOf(data);
  if (!rows.length) return <SectionEmpty text={t("nothing_here")} />;

  return (
    <SectionTable
      minWidth={1040}
      rows={newestFirst(rows)}
      onRow={onOpen}
      columns={[
        { key: "id", title: t("orders_payment_id_label"), dataIndex: "id", width: 120, render: value => <b>#{value}</b> },
        { key: "cp", title: t("orders_popup_counterparty_label"), dataIndex: ["counterparty", "name"], width: 240, ellipsis: true },
        /* ⚠ `debt.name` does not exist — `Debt` is serialised as `(id, name_of_the_document)`, so
           this column was blank on every row and read as the empty gap QA reported. */
        { key: "debt", title: t("orders_payment_debt_label"), dataIndex: ["debt", "name_of_the_document"], width: 180, ellipsis: true },
        {
          key: "amount",
          title: t("orders_payment_amount_label"),
          dataIndex: "amount",
          width: 130,
          align: "right",
          render: value => <span className="vd-num">{money(value)}</span>,
        },
        { key: "at", title: t("orders_product_status_date"), dataIndex: "datetime", width: 160, render: value => formatDateTime(value, lang) },
        { key: "comment", title: t("orders_popup_comment_label"), dataIndex: "comment", ellipsis: true, render: value => value || dash },
        openColumn(t("view_details")),
      ]}
    />
  );
}

/* ------------------------------------------------------------------ reports */

/**
 * `photos_in_visit` proxies Outforz and joins picsell `Approval` rows onto the page it fetched —
 * the same objects and the same review state as /photo-review.
 *
 * ⚠ The rows go through the `PhotoReview` model, and that is not decoration. `photo` is NOT an API
 * field: the payload carries `url_photo`, or `ticket_contents[].url_photo` for a grouped ticket, and
 * `imgGroup` is the only thing that flattens either into the array both this grid and the review
 * modal read. Rendering the raw rows is why every cover here was a placeholder glyph. It is also
 * exactly the shape `ReviewModal` needs, so the same wrapping is what lets this page open the
 * report the same way /photo-review does instead of in a generic details dialog.
 */
export function ReportsSection({ data, lang, pager, onOpen }) {
  const t = key => translate(VISIT_PAGE, key, lang);
  const rows = useMemo(() => rowsOf(data).map(row => new PhotoReview(row)), [data]);
  if (!rows.length) return <SectionEmpty text={t("nothing_here")} />;

  return (
    <Flex vertical gap="middle">
      <div className="vd-rgrid">
        {rows.map((item, index) => {
          const photo = item.photo?.[0];
          const title = item.additional_info?.title || item.ticket || `#${item.id}`;
          return (
            <Card
              key={item.id ?? index}
              className="vd-rcard"
              variant="outlined"
              hoverable
              /* The whole loaded page goes over, so the modal can step it. */
              onClick={() => onOpen(rows, index)}
              cover={
                <div className="vd-rcard__c">
                  {/* ⚠ `rootClassName` is load-bearing: antd wraps the `<img>` in a `span.ant-image`,
                      so sizing only the inner element leaves the wrapper at the photo's intrinsic
                      size and the cover box never applies. Same trap `review-modal.scss` records. */}
                  {photo ? (
                    <Image src={photo} alt="" preview={false} rootClassName="vd-rcard__imgwrap" />
                  ) : (
                    <FileImageOutlined className="vd-rcard__none-icon" />
                  )}
                  <StatusTag status={statusOf(item.approval)} lang={lang} className="vd-rcard__pill" />
                </div>
              }>
              <Typography.Text strong ellipsis={{ tooltip: title }} className="vd-rcard__t">
                {title}
              </Typography.Text>
              {/* Chips, not a dot-joined string: the same tags are chips in the review modal this
                  card opens into, and low-contrast run-on text was indistinguishable from the
                  caption above it. A single tag arrives as a bare string, not an array. */}
              <Space size={4} wrap className="vd-rcard__tags">
                {tagsOf(item.additional_info?.tags).map((tag, i) => (
                  <Tag key={`${tag}-${i}`} className="vd-rcard__tag">
                    {tag}
                  </Tag>
                ))}
              </Space>
            </Card>
          );
        })}
      </div>
      {/* Outforz pages this at 25 and the proxy forwards nothing but `page`, so there is no
          rows-per-page control to offer — `pager.sizes` is undefined and the footer omits it. */}
      <SectionFoot id="vd-reports" total={data?.count ?? rows.length} pager={pager} />
    </Flex>
  );
}

/* ------------------------------------------------------------------ tasks */

const TASK_SIZES = [10, 25, 50];

/**
 * Tasks. Paged CLIENT-side, because the whole list is already here: `tasks_in_visit/` declares a
 * pagination class and then ignores it, serialising `visit.available_tasks.all()` into a bare
 * array. So a request per page would fetch the same everything again — the rows are sliced instead,
 * and the footer's total is the real one rather than a page's worth.
 */
export function TasksSection({ data, lang, onOpen }) {
  const t = key => translate(VISIT_PAGE, key, lang);
  const all = rowsOf(data);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(TASK_SIZES[1]);
  const rows = all.slice((page - 1) * size, page * size);
  if (!all.length) return <SectionEmpty text={t("nothing_here")} />;

  return (
    <Flex vertical gap="middle">
      <SectionTable
        minWidth={1000}
        rows={rows}
        onRow={onOpen}
        columns={[
          {
            key: "status",
            title: t("task_tab_answer_status_label"),
            width: 150,
            render: (value, row) => <StatusTag status={statusOf(row.answer?.approval)} lang={lang} />,
          },
          { key: "template", title: t("task_tab_type_label"), dataIndex: ["template", "name"], width: 170, ellipsis: true },
          {
            key: "title",
            title: t("task_tab_task_name_label"),
            dataIndex: "title",
            ellipsis: true,
            render: (value, row) => (
              <Flex vertical className="vd-cell">
                <Typography.Text ellipsis={{ tooltip: value }}>{value}</Typography.Text>
                <Typography.Text type="secondary" className="vd-cell__s" ellipsis={{ tooltip: row.description }}>
                  {row.description}
                </Typography.Text>
              </Flex>
            ),
          },
          { key: "end", title: t("task_tab_deadline_label"), dataIndex: "end", width: 150, render: value => (value ? formatDate(value, lang) : dash) },
          {
            key: "photos",
            title: t("task_tab_count_photo_label"),
            dataIndex: "image_count",
            width: 100,
            align: "center",
            render: value => (value ? <span className="vd-num">{value}</span> : dash),
          },
          {
            key: "answer",
            title: t("task_tab_answer_comment_label"),
            width: 200,
            ellipsis: true,
            render: (value, row) => row.answer?.answer || <span className="vd-dash">{t("no_answer")}</span>,
          },
          openColumn(t("view_details")),
        ]}
      />
      <SectionFoot
        id="vd-tasks"
        total={all.length}
        pager={{
          page,
          size,
          sizes: TASK_SIZES,
          labels: { perPage: t("rows_per_page"), range: t("range_of") },
          onPage: setPage,
          onSize: next => {
            setSize(next);
            setPage(1);
          },
        }}
      />
    </Flex>
  );
}

/* ------------------------------------------------------------- price monitoring */

const PRICE_SEGMENTS = [
  ["all", "f_all"],
  ["pending", "on_verification_single_visit_name"],
  ["approved", "approved_single_visit_name"],
  ["rejected", "no_approved_single_visit_name"],
];

export function PriceSection({ data, lang, pager, onOpen }) {
  const t = key => translate(VISIT_PAGE, key, lang);
  const all = rowsOf(data);
  const total = data?.count ?? all.length;
  const [status, setStatus] = useState("all");
  const [query, setQuery] = useState("");

  /* ⚠ The segment and the search filter the LOADED PAGE, not the section. This endpoint paginates
     (200 a page by default) and carries no status counts, so a server-side filter is not available
     to ask for — the same constraint /photo-review's client-side status segment lives under. The
     toolbar says which of the two numbers is which rather than reporting the page as the total,
     which is what it used to do. */
  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return all.filter(row => {
      if (status !== "all" && statusOf(row.approval) !== status) return false;
      if (!needle) return true;
      return `${row.product_name || ""} ${row.product_code || ""}`.toLowerCase().includes(needle);
    });
  }, [all, status, query]);

  const countOf = key => (key === "all" ? all.length : all.filter(row => statusOf(row.approval) === key).length);

  return (
    <Flex vertical gap="middle">
      <Flex className="vd-toolbar" align="center" gap="small" wrap>
        <Segmented
          value={status}
          onChange={setStatus}
          options={PRICE_SEGMENTS.map(([value, key]) => ({
            value,
            label: (
              <span>
                {t(key)} <span className="vd-seg__n">{countOf(value)}</span>
              </span>
            ),
          }))}
        />
        <Input
          className="vd-toolbar__search"
          value={query}
          allowClear
          prefix={<SearchOutlined />}
          aria-label={t("monitoring_tab_pname_label")}
          onChange={event => setQuery(event.target.value)}
        />
        <span className="vd-spacer" />
        <Typography.Text type="secondary">
          {fill(t(total > all.length ? "filtered_of_page" : "filtered_of"), { n: rows.length, total: all.length })}
        </Typography.Text>
      </Flex>

      {rows.length ? (
        <SectionTable
          minWidth={900}
          rows={rows}
          onRow={onOpen}
          columns={[
            {
              key: "photo",
              title: "",
              width: 66,
              render: (value, row) => (row.photo_url ? <Image src={row.photo_url} alt="" width={46} height={34} className="vd-thumb" preview={false} /> : dash),
            },
            {
              key: "product",
              title: t("monitoring_tab_pname_label"),
              ellipsis: true,
              render: (value, row) => (
                <Flex vertical className="vd-cell">
                  <Typography.Text ellipsis={{ tooltip: row.product_name }}>{row.product_name}</Typography.Text>
                  <Typography.Text type="secondary" className="vd-cell__s">
                    {row.product_code}
                  </Typography.Text>
                </Flex>
              ),
            },
            {
              key: "price",
              title: t("monitoring_tab_price_label"),
              dataIndex: "price",
              width: 120,
              align: "right",
              render: value => <span className="vd-num">{money(value)}</span>,
            },
            {
              key: "sale",
              title: t("monitoring_tab_sale_price_label"),
              dataIndex: "sale_price",
              width: 130,
              align: "right",
              render: value => <span className="vd-num">{money(value)}</span>,
            },
            {
              key: "delta",
              title: t("delta"),
              width: 100,
              align: "right",
              render: (value, row) => <Delta price={row.price} salePrice={row.sale_price} />,
            },
            { key: "status", title: t("status_label"), width: 150, render: (value, row) => <StatusTag status={statusOf(row.approval)} lang={lang} /> },
            openColumn(t("view_details")),
          ]}
        />
      ) : (
        <SectionEmpty text={t("nothing_here")} />
      )}
      {/* The pager is the section's, so it stays put while the client-side filter above narrows
          what is drawn from the page it fetched. */}
      <SectionFoot id="vd-price" total={total} pager={pager} />
    </Flex>
  );
}

/** Sale price against list price. A drop is good news for the shopper, so it is the success tone;
    the arithmetic is only shown when both numbers exist. */
export function Delta({ price, salePrice }) {
  if (price === null || price === undefined || salePrice === null || salePrice === undefined) return dash;
  const value = Number(salePrice) - Number(price);
  if (!value) return dash;
  return <span className={`vd-num vd-delta vd-delta--${value < 0 ? "down" : "up"}`}>{`${value > 0 ? "+" : ""}${value.toFixed(2)}`}</span>;
}

/* ------------------------------------------------------------------ surveys */

/**
 * Surveys: an accordion, one survey expanded at a time, each loading its own answers.
 *
 * The legacy tab rendered every survey fully expanded with all four group tabs mounted — with 14
 * surveys × up to 12 entities × up to 6 questions that is thousands of rows in the DOM. Here the
 * list costs one request and each survey costs one more, only when opened.
 */
export function SurveysSection({ visitId, data, lang }) {
  const t = key => translate(VISIT_PAGE, key, lang);
  /* Only surveys with something recorded. `surveys_in_visit` lists every survey TARGETING the
     point, answered or not, and an untouched one is noise in a review queue — the legacy tab
     filtered the same way. The summary counts above still describe all of them. */
  const surveys = (data?.surveys || []).filter(survey => (survey.answers_count ?? 0) > 0 || (survey.photos_count ?? 0) > 0);
  const summary = data?.summary || {};
  const [open, setOpen] = useState(null);
  const [detail, setDetail] = useState({});
  const [loading, setLoading] = useState(null);

  const toggle = survey => {
    if (open === survey.id) {
      setOpen(null);
      return;
    }
    setOpen(survey.id);
    if (detail[survey.id]) return;
    setLoading(survey.id);
    call({ method: "GET", url: `visit_surveys/${survey.id}/?visit_id=${visitId}` }, response => {
      setLoading(null);
      setDetail(prev => ({ ...prev, [survey.id]: response || {} }));
    });
  };

  if (!surveys.length) return <SectionEmpty text={t("nothing_here")} />;

  return (
    <Flex vertical gap="middle">
      <Flex className="vd-svstats" gap="middle" wrap>
        {[
          [summary.surveys, t("surveys_summary_surveys_label")],
          [summary.answers, t("surveys_summary_answers_label")],
          [summary.photos, t("surveys_summary_photos_label")],
        ].map(([value, label]) => (
          <Flex vertical key={label} className="vd-svstat">
            <span className="vd-svstat__n">{value ?? 0}</span>
            <Typography.Text type="secondary" className="vd-svstat__l">
              {label}
            </Typography.Text>
          </Flex>
        ))}
      </Flex>

      <Flex vertical gap="small">
        {surveys.map(survey => (
          <SurveyCard
            key={survey.id}
            survey={survey}
            lang={lang}
            open={open === survey.id}
            loading={loading === survey.id}
            detail={detail[survey.id]}
            onToggle={() => toggle(survey)}
          />
        ))}
      </Flex>
    </Flex>
  );
}

/* `visit_surveys/:id/` groups the answers four ways, and the fourth is NOT shaped like the others:
   products / brands / categories are blocks of `{ <entity>: {...}, answers: [...] }`, while `other`
   is a FLAT list of answers with no entity to hang them under. The entity's name is nested under
   its own key — reading `entity.name` (as this did) is undefined for all three, which is why every
   header rendered blank. */
const GROUPS = [
  ["products", "surveys_tab_products", "product"],
  ["brands", "surveys_tab_brands", "brand"],
  ["categories", "surveys_tab_categories", "category"],
  ["other", "surveys_tab_other", null],
];

function SurveyCard({ survey, lang, open, loading, detail, onToggle }) {
  const t = key => translate(VISIT_PAGE, key, lang);
  const [group, setGroup] = useState("products");
  const groups = detail?.groups || {};
  const entities = groups[group] || [];
  const entityKey = GROUPS.find(([key]) => key === group)?.[2];

  return (
    <Card className={`vd-sv${open ? " vd-sv--open" : ""}`} variant="outlined" size="small">
      <button type="button" className="vd-sv__head" aria-expanded={open} onClick={onToggle}>
        <Typography.Text strong className="vd-sv__t">
          {survey.title || survey.name}
        </Typography.Text>
        <Typography.Text type="secondary" className="vd-sv__m">
          {survey.answers_count ?? 0} · {t("surveys_summary_answers_label")}
        </Typography.Text>
      </button>

      {open ? (
        <div className="vd-sv__body">
          <Segmented
            value={group}
            onChange={setGroup}
            options={GROUPS.map(([key, labelKey]) => ({
              value: key,
              label: (
                <span>
                  {t(labelKey)} <span className="vd-seg__n">{(groups[key] || []).length}</span>
                </span>
              ),
            }))}
          />
          {loading ? (
            <Typography.Text type="secondary">{translate(ENTITY_DROPDOWN_SETTINGS, "option_loading", lang)}</Typography.Text>
          ) : !entities.length ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("surveys_no_results")} />
          ) : entityKey ? (
            <Flex vertical gap="small" className="vd-sv__ents">
              {entities.map((entity, index) => (
                <SurveyEntity key={entity[entityKey]?.id ?? index} entity={entity} entityKey={entityKey} lang={lang} />
              ))}
            </Flex>
          ) : (
            /* `other` is already the answers — there is no entity to collapse it under. */
            <AnswerRows answers={entities} lang={lang} />
          )}
        </div>
      ) : null}
    </Card>
  );
}

function SurveyEntity({ entity, entityKey, lang }) {
  const [open, setOpen] = useState(false);
  const answers = entity.answers || [];
  const subject = entity[entityKey] || {};

  return (
    <div className={`vd-ent${open ? " vd-ent--open" : ""}`}>
      <button type="button" className="vd-ent__h" aria-expanded={open} onClick={() => setOpen(value => !value)}>
        <Typography.Text strong>{subject.name || dash}</Typography.Text>
        {subject.code ? (
          <Typography.Text type="secondary" className="vd-ent__c">
            {subject.code}
          </Typography.Text>
        ) : null}
        <span className="vd-spacer" />
        <Typography.Text type="secondary">{answers.length}</Typography.Text>
      </button>
      {open ? <AnswerRows answers={answers} lang={lang} /> : null}
    </div>
  );
}

function AnswerRows({ answers, lang }) {
  return (
    <div className="vd-qa">
      {answers.map((answer, index) => (
        <div className="vd-qa__row" key={answer.id ?? index}>
          {/* `question` is an OBJECT here. Rendering it directly threw "Objects are not valid as a
              React child", which took the whole section down the moment an entity was expanded. */}
          <Typography.Text className="vd-qa__q">{answer.question?.text || dash}</Typography.Text>
          <span className="vd-qa__a">
            <AnswerValue answer={answer} lang={lang} />
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * One answer's value.
 *
 * A survey answer is stored in a column PER TYPE (`answer_text`, `answer_numeric`,
 * `answer_dropdown`, `answer_date`, `answer_boolean`, `photos`) and `question.answer_type` says
 * which one holds it — there is no generic `answer` / `value` field, which is why every non-photo
 * answer used to render as a dash.
 */
function AnswerValue({ answer, lang }) {
  const t = key => translate(VISIT_PAGE, key, lang);
  const photos = (answer.photos || []).map(photo => photo.photo).filter(Boolean);

  switch (answer.question?.answer_type) {
    case "boolean":
      /* Tri-state: null is "not answered", which is not the same statement as "no".
         Plain text, not a status tag — the legacy tab painted "No" red, but a negative survey
         answer is data and may well be the expected one; only an approval verdict earns a tone. */
      if (answer.answer_boolean === null || answer.answer_boolean === undefined) return dash;
      return t(answer.answer_boolean ? "surveys_answer_bool_yes" : "surveys_answer_bool_no");
    case "numeric":
      return answer.answer_numeric === null || answer.answer_numeric === undefined ? dash : <span className="vd-num">{answer.answer_numeric}</span>;
    case "dropdown":
      return answer.answer_dropdown?.label || dash;
    case "text":
      return answer.answer_text || dash;
    case "date":
      return answer.answer_date ? formatDate(answer.answer_date, lang) : dash;
    case "photo":
      if (!photos.length) return dash;
      /* antd's own lightbox, with arrows and a counter — the same thing /answers and /task use
         rather than a hand-rolled overlay. */
      return (
        <Image.PreviewGroup>
          <Flex gap={4} wrap>
            {photos.map((src, index) => (
              <Image key={index} src={src} alt="" width={64} className="vd-thumb" />
            ))}
          </Flex>
        </Image.PreviewGroup>
      );
    default:
      /* An unknown type is still worth showing whatever was recorded rather than nothing. */
      return answer.answer_text || answer.answer_dropdown?.label || dash;
  }
}

/* ------------------------------------------------------------------ GPS + log */

/** The recorded track lives on the visit itself (`visit.meta`), so this section costs no request. */
export function GpsSection({ visit, lang }) {
  const t = key => translate(VISIT_PAGE, key, lang);
  const track = useMemo(() => trackOf(visit), [visit]);
  const point = visit?.point?.location;

  const stats = [
    [t("point_started_title"), formatTime(visit?.start, lang)],
    [t("visit_end"), formatTime(visit?.end, lang)],
    [t("gps_points"), track.length || dash],
    [t("point"), visit?.point?.name],
  ];

  return (
    <div className="vd-gps">
      <div>{point ? <LeafletMap lat={point.latitude} lng={point.longitude} track={track} height={320} /> : <SectionEmpty text={t("no_coordinates")} />}</div>
      <Flex vertical gap="small" className="vd-gps__stats">
        {stats.map(([label, value]) => (
          <Flex key={label} align="center" justify="space-between" gap="small" className="vd-gps__stat">
            <Typography.Text type="secondary">{label}</Typography.Text>
            <Typography.Text strong>{value || dash}</Typography.Text>
          </Flex>
        ))}
      </Flex>
    </div>
  );
}

/**
 * `visit.meta` is the recorded track — a free-form JSON list written only by the mobile app, with
 * no shape enforced anywhere in the API, so accept every plausible one rather than assume.
 *
 * It arrives only on the single-visit endpoint, from `VisitDetailSerializer`. Every other visit
 * serializer excludes it — a track per row would ride along on the visits list and on the visit
 * nested in every answer — so it is absent from a list row, and this section is the one consumer.
 */
function trackOf(visit) {
  const meta = parseMeta(visit?.meta);
  return meta
    .map(entry => {
      if (Array.isArray(entry) && entry.length >= 2) return [Number(entry[0]), Number(entry[1])];
      if (entry && typeof entry === "object") {
        const lat = entry.latitude ?? entry.lat;
        const lng = entry.longitude ?? entry.lng ?? entry.lon;
        if (lat !== undefined && lng !== undefined) return [Number(lat), Number(lng)];
      }
      return null;
    })
    .filter(pair => pair && Number.isFinite(pair[0]) && Number.isFinite(pair[1]));
}

/** A JSONField can hand back a string rather than a list, and `models/visit.js` has always
    described this field as one — so parse that case instead of silently drawing nothing. */
function parseMeta(meta) {
  if (Array.isArray(meta)) return meta;
  if (typeof meta !== "string" || !meta) return [];
  try {
    const parsed = JSON.parse(meta);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function LogSection({ data, lang }) {
  const t = key => translate(VISIT_PAGE, key, lang);
  const rows = rowsOf(data);
  if (!rows.length) return <SectionEmpty text={t("nothing_here")} />;

  return (
    <Timeline
      className="vd-log"
      items={rows.map(entry => ({
        key: entry.id,
        children: (
          <Flex vertical gap={2}>
            <Typography.Text>{entry.additional_info?.event || entry.action}</Typography.Text>
            <Flex align="center" gap="small">
              <Typography.Text type="secondary" className="vd-log__m">
                {formatDateTime(entry.timestamp, lang)}
              </Typography.Text>
              {entry.user ? (
                /* The same avatar as every other person in the app: initials, photo preferred.
                   28px rather than the log's old 20 because the initials are the 12px scale
                   floor and two characters at 12px do not fit a smaller circle (spec/01#type). */
                <PersonCell user={entry.user} strong={false} />
              ) : null}
            </Flex>
          </Flex>
        ),
      }))}
    />
  );
}

/* ------------------------------------------------------------------ dates */

/* Intl has no hy-AM / ka-GE data in some Chrome builds and silently resolves to en-US; `localeFor`
   is the app's mapping and en-GB is the day-first fallback the rest of the redesign uses. */
import { localeFor } from "../merchandiser/route/dateUtils";

const fmt = (value, lang, options) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(localeFor(lang), options).format(date);
};

export const formatDate = (value, lang) => fmt(value, lang, { day: "2-digit", month: "2-digit", year: "numeric" });
export const formatDateTime = (value, lang) => fmt(value, lang, { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
export const formatTime = (value, lang) => fmt(value, lang, { hour: "2-digit", minute: "2-digit" });

/* Kept beside the sections that render them: the approve/reject pair the queue rows carry, so a
   verdict always reads the same wherever it is offered.

   It owns its own <Flex> rather than returning a Fragment. A Fragment looks equivalent and is not:
   antd's <Space> splits its own DIRECT children into `.ant-space-item`s, and a function-component
   element is ONE child however many nodes it returns — so both buttons landed in a single item with
   the gap outside them, touching, and the ≤620px `.ant-btn { flex: 1 }` rule had a non-flex parent
   to stretch against. */
export function VerdictButtons({ lang, size = "small", className = "", onApprove, onReject, busy }) {
  const t = key => translate(VISIT_PAGE, key, lang);
  return (
    <Flex className={className} align="center" gap={8}>
      <Button size={size} danger icon={<CloseCircleOutlined />} loading={busy} onClick={onReject}>
        {t("reject_btn")}
      </Button>
      <Button size={size} className="vd-btn--ok" icon={<CheckCircleOutlined />} loading={busy} onClick={onApprove}>
        {t("approv_btn")}
      </Button>
    </Flex>
  );
}
